// ════════════════════════════════════════════════════════════════════
//  MISSION CONTENT — All text for puzzle stages, Leon dialogues,
//  tutorials, HUD labels, and system messages.
//  Edit strings here to update them across the whole site.
// ════════════════════════════════════════════════════════════════════
import CONFIG from '@/config/siteConfig';

// ─── Stage 1: Cinematic Intro ───
export const CINEMATIC_INTRO = {
  bgImage: 'https://static.prod-images.emergentagent.com/jobs/febf00d3-02f1-4680-9d85-457affdefbb8/images/b0571656e516b431e26e7c676b2c0cb811a3f266a84471608112d35836aea643.png',
  briefingLines: [
    { type: 'header', text: 'CLASSIFIED TRANSMISSION', delay: 2200 },
    { type: 'sub', text: 'UMBRELLA CORPORATION // SPECIAL OPERATIONS DIVISION', delay: 3200 },
    { type: 'divider', delay: 4000 },
    { type: 'field', label: 'DATE', value: 'MARCH 22, 2026', delay: 4600 },
    { type: 'field', label: 'CLEARANCE', value: 'OMEGA', delay: 5400 },
    { type: 'field', label: 'OPERATIVE', value: CONFIG.recipientName.toUpperCase(), highlight: true, delay: 6200 },
    { type: 'stamp', delay: 7000 },
    { type: 'divider', delay: 7800 },
    { type: 'narrative', text: 'You have been selected.', delay: 8400 },
    { type: 'narrative', text: 'A high-priority mission is now in your hands.', delay: 9600 },
    { type: 'narrative', text: 'Your handler will guide you every step of the way.', delay: 10800 },
    { type: 'divider', delay: 11600 },
    { type: 'emphasis', text: 'Stay sharp. Trust the process. Trust Leon.', delay: 12200 },
  ],
  sysTicker: [
    'SECTOR 7-G: Containment stable',
    'T-Virus containment: 99.7%',
    'Security cameras: 24 active',
    'Personnel clearance: OMEGA required',
    'Bioweapon samples: 47 catalogued',
    'Generator output: 84%',
    'Emergency exits: SEALED',
    'Specimen transfer: SCHEDULED 0300',
  ],
  dataFragments: [
    'CLASSIFIED', 'OMEGA-7', '0xFE4A2B', 'SECTOR-G', 'PRIORITY-1',
    'BIOHAZARD', 'LEVEL-4', '0xAA91CF', 'REDACTED', 'ALPHA-TEAM',
    'T-VIRUS', '0x00FF41', 'LOCKDOWN', 'HIVE-NET', 'ENCRYPT',
  ],
  topBar: {
    secureChannel: 'SECURE CHANNEL',
    encrypted: 'ENCRYPTED',
  },
  signal: {
    locked: 'SIGNAL LOCKED',
    acquiring: 'ACQUIRING SIGNAL...',
    incoming: 'INCOMING TRANSMISSION',
  },
  commLink: {
    established: 'COMM LINK ESTABLISHED',
    incoming: 'INCOMING COMM LINK...',
  },
  stamp: 'CLASSIFIED',
  missionLoaded: 'MISSION PARAMETERS LOADED',
  readyPrompt: `Are you ready, ${CONFIG.recipientCodename}?`,
  beginButton: 'Begin Mission',
  leon: {
    intro: "Welcome aboard, Agent. I've been assigned as your handler for this operation. The mission ahead won't be easy... but I have a feeling you're more than capable. Stay sharp.",
  },
};

// ─── Stage 2: Mission Briefing ───
export const MISSION_BRIEFING = {
  objectives: [
    'Navigate through 11 secured checkpoints within the facility',
    'Collect all access items from each checkpoint',
    'Unlock the Main Vault using all collected items',
    'Retrieve the classified birthday package',
  ],
  terms: [
    { text: 'The operative agrees to be celebrated unconditionally.', color: 'text-gray-400' },
    { text: 'The operative must accept all expressions of love and appreciation.', color: 'text-gray-400' },
    { text: 'The operative will receive unlimited birthday wishes for the next 24 hours.', color: 'text-gray-400' },
    { text: 'Handler Leon reserves the right to be annoyingly supportive throughout the mission.', color: 'text-gray-400' },
    //{ text: `${CONFIG.senderName} has authorized infinite celebrations in your honor.`, color: 'text-pink-400/80', special: true },
  ],
  sysMessages: [
    'DOCUMENT INTEGRITY: VERIFIED',
    'ENCRYPTION: AES-256-GCM ACTIVE',
    'CLEARANCE LEVEL: OMEGA',
    'BIOMETRIC LOCK: ENGAGED',
    'SURVEILLANCE: DISABLED IN SECTOR',
    'HANDLER CHANNEL: SECURE',
    'OPERATIVE STATUS: AWAITING AUTHORIZATION',
  ],
  decryptPaths: [
    '/sys/vault/omega/doc-2026.enc',
    '/usr/umbrella/classified/briefing.dat',
    '/sec/clearance/omega-level.key',
    '/opt/mission/birthday-op.cfg',
    '/var/auth/biometric-seal.bin',
  ],
  decrypt: {
    decrypting: '◈ DECRYPTING DOCUMENT...',
    complete: '✓ DECRYPTION COMPLETE',
  },
  topBar: {
    label: 'Document Viewer // Omega Clearance',
    docId: 'DOC-OMEGA-2026',
  },
  terminalHeader: 'MISSION BRIEFING // CLASSIFIED',
  docId: 'DOC-OMEGA-2026',
  header: {
    title: 'Umbrella Corporation',
    subtitle: 'Classified Document // Level Omega Clearance',
    alert: 'ALERT',
  },
  details: [
    { label: 'Mission', value: 'OPERATION: BIRTHDAY', color: 'text-yellow-500', icon: '>' },
    { label: 'Operative', value: `AGENT: ${CONFIG.recipientName.toUpperCase()}`, color: 'text-red-400', icon: '*', isOperative: true },
    { label: 'Handler', value: 'LEON S. KENNEDY', color: 'text-green-400', icon: '#' },
    { label: 'Authorized', value: CONFIG.senderName.toUpperCase(), color: 'text-pink-400', icon: '~' },
  ],
  objectivesHeading: 'Mission Objectives',
  termsHeading: 'Terms & Conditions',
  penalty: 'PENALTY FOR NON-COMPLIANCE: Extra hugs and even more love',
  biometricAuth: 'Biometric Authorization Required',
  signatureRequired: 'Signature Required',
  acceptButton: 'Accept Mission',
  acceptedStamp: 'Mission Accepted',
  authText: 'OPERATIVE AUTHORIZED // CLEARANCE GRANTED',
  footerQuote: `"By accepting, you acknowledge being the most incredible person in existence." -- ${CONFIG.senderName}`,
  leon: {
    intro: `Read this briefing carefully, ${CONFIG.recipientName}. This mission was written specifically for you. Every checkpoint ahead was designed with you in mind. I'll be with you every step of the way.`,
  },
};

// ─── Stage 3: Password Gate ───
export const PASSWORD_GATE = {
  bgImage: 'https://static.prod-images.emergentagent.com/jobs/febf00d3-02f1-4680-9d85-457affdefbb8/images/b0571656e516b431e26e7c676b2c0cb811a3f266a84471608112d35836aea643.png',
  bootLines: [
    { text: '██╗   ██╗██████╗  ██████╗ ███████╗', delay: 0, type: 'ascii' },
    { text: '██║   ██║██╔══██╗██╔════╝ ██╔════╝', delay: 50, type: 'ascii' },
    { text: '██║   ██║██████╔╝██║      ███████╗', delay: 100, type: 'ascii' },
    { text: '╚██╗ ██╔╝██╔══██╗██║      ╚════██║', delay: 150, type: 'ascii' },
    { text: ' ╚████╔╝ ██████╔╝╚██████╗ ███████║', delay: 200, type: 'ascii' },
    { text: '  ╚═══╝  ╚═════╝  ╚═════╝ ╚══════╝', delay: 250, type: 'ascii' },
    { text: '', delay: 400, type: 'blank' },
    { text: 'UMBRELLA CORP. SECURE TERMINAL v4.2.1', delay: 500, type: 'header' },
    { text: 'Copyright (c) 1968-2026 Umbrella Corporation', delay: 700, type: 'dim' },
    { text: '', delay: 900, type: 'blank' },
    { text: 'Initializing biometric subsystem...       [OK]', delay: 1100, type: 'ok' },
    { text: 'Loading cryptographic protocols...        [OK]', delay: 1600, type: 'ok' },
    { text: 'Connecting to HIVE network...             [OK]', delay: 2100, type: 'ok' },
    { text: 'Containment protocols...                  [ACTIVE]', delay: 2500, type: 'warn' },
    { text: 'Threat detection level...                 [LEVEL 4]', delay: 2900, type: 'danger' },
    { text: 'Biohazard containment integrity...        [99.7%]', delay: 3300, type: 'ok' },
    { text: '', delay: 3600, type: 'blank' },
    { text: '> AWAITING SECURITY CODE INPUT_', delay: 3800, type: 'prompt' },
  ],
  sysMessages: [
    'SECTOR 7-G: Containment stable',
    'Bioweapon samples: 47 catalogued',
    'Personnel clearance: OMEGA required',
    'T-Virus containment: 99.7% integrity',
    'Security camera feed: 24 active',
    'Elevator shaft B: LOCKED',
    'Lab 3 temperature: -12.4°C',
    'Network intrusion attempts: 0',
    'Generator output: 84% capacity',
    'Specimen transfer: SCHEDULED 0300',
    'Ventilation: Filtered Mode ACTIVE',
    'Emergency exits: SEALED',
  ],
  boot: {
    systemInit: 'SYSTEM INITIALIZATION',
  },
  hud: {
    live: 'LIVE',
    threatLevel: 'THREAT LEVEL:',
    intruderDetection: 'INTRUDER DETECTION ACTIVE',
    cam: 'CAM-07',
    signal: 'SIGNAL:',
    biohazard: 'BIOHAZARD: ACTIVE',
  },
  terminal: {
    header: 'UMBRELLA SECURITY TERMINAL',
    lockdown: 'SYS //LOCKDOWN',
    title1: 'Umbrella',
    title2: 'Corporation',
    classifiedAccess: 'Classified Access',
    securityCode: 'Security Code',
    authenticate: 'Authenticate',
  },
  status: {
    granted: 'ACCESS GRANTED',
    denied: 'ACCESS DENIED',
    deniedFull: 'ACCESS DENIED — INTRUDER ALERT',
    scanning: 'AUTHENTICATING...',
    idle: 'AWAITING INPUT',
    verifying: 'VERIFYING BIOMETRIC SIGNATURE...',
  },
  stats: [
    { label: 'Attempts', key: 'attempts' },
    { label: 'Protocol', value: 'LAMBDA' },
    { label: 'Threat', valueCritical: 'CRITICAL', valueDefault: 'MAXIMUM' },
  ],
  footer: 'Biohazard Containment Level 4',
  bottomLabel: 'Umbrella Corporation Data Systems v4.2.1',
};

// ─── Stage 4: Shooting Gallery ───
export const SHOOTING_GALLERY = {
  checkpoint: 'Checkpoint 02',
  title: 'Firing Range',
  subtitle: 'Target Practice // Eliminate Threats',
  targetTypes: [
    { type: 'zombie', points: 1, color: '#ef4444', size: 56, duration: 2200, label: 'ZOMBIE' },
    { type: 'licker', points: 2, color: '#a855f7', size: 48, duration: 1600, label: 'LICKER' },
    { type: 'crow', points: 1, color: '#6b7280', size: 40, duration: 1800, label: 'CROW' },
    { type: 'tyrant', points: 3, color: '#dc2626', size: 68, duration: 2800, label: 'TYRANT' },
  ],
  hud: {
    firingRange: 'FIRING RANGE',
    threat: 'THREAT:',
    threatLevels: { moderate: 'MODERATE', high: 'HIGH', critical: 'CRITICAL' },
    combo: 'x COMBO',
    score: 'SCORE',
    targets: 'TARGETS',
  },
  tutorial: {
    heading: 'Mission Brief',
    body: 'Eliminate hostiles as they appear. Click targets before they vanish.',
    hostileTypes: 'Hostile Types',
    rules: 'Rules of Engagement',
    ruleItems: [
      { label: 'TARGET SCORE', value: `${48} points to clear the range` },
      { label: 'COMBO', value: 'Hit targets in succession for a combo streak' },
      { label: 'MAX TARGETS', value: `Up to ${5} hostiles on screen at once` },
    ],
    startButton: 'Start Shooting',
  },
  completion: {
    heading: 'RANGE CLEARED',
    itemText: 'Tactical Scope acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `Time for some target practice, ${CONFIG.recipientName}. Hostiles will appear on screen -- click them before they vanish. Score 48 points to clear the range. Watch out for Tyrants, they're worth more but move slower. Stay sharp!`,
    complete: `Nice shooting! Score: {score} points. You've got a steady hand, ${CONFIG.recipientName}. Tactical Scope acquired -- this'll come in handy. Let's keep moving.`,
  },
};

// ─── Stage 5: Word Scramble ───
export const PUZZLE_1_WORD_SCRAMBLE = {
  checkpoint: 'Checkpoint 01 // Data Recovery',
  title: 'Data Recovery',
  subtitle: 'Scrambled Intel // Decode the classified words',
  words: ['PARTY', 'NUMNOM', 'BIRTHDAY', 'NURSE', 'KITTEN', 'JOUDIE'],
  wordHints: [
    'Hints removed for difficulty...',
    'Hints removed for difficulty...',
    'Hints removed for difficulty...',
    'Hints removed for difficulty...',
    'Hints removed for difficulty...',
    'Hints removed for difficulty...',
  ],
  wordFiles: [
    'INTERCEPT-A7.dat',
    'INTERCEPT-B3.dat',
    'INTERCEPT-C1.dat',
    'INTERCEPT-D9.dat',
    'INTERCEPT-E1.dat',
    'INTERCEPT-F2.dat',
  ],
  tutorial: {
    heading: 'DATA RECOVERY',
    subheading: 'SCRAMBLED INTEL DECRYPTION',
    procedure: 'PROCEDURE',
    steps: [
      { n: '01', text: 'Each file contains a SCRAMBLED WORD — tap letters in the right order' },
      { n: '02', text: 'Use the HINT to figure out what the word is' },
      { n: '03', text: 'Tap a placed letter to REMOVE it — or hit RESET to start over' },
      { n: '04', text: 'Decode all files to recover the intel' },
    ],
    example: 'EXAMPLE',
    exampleHint: 'Man\'s best friend...',
    exampleWord: ['D', 'O', 'G'],
    exampleStatus: 'DECODED',
    warning: 'WRONG SPELLING? NO PENALTY — JUST RESET AND TRY AGAIN',
    startButton: 'BEGIN DECRYPTION',
  },
  puzzle: {
    fileLabel: 'Encrypted File',
    signalIntercept: 'SIGNAL INTERCEPT:',
    selectLetters: 'SELECT LETTERS',
    resetButton: 'RESET WORD',
    decoded: 'Decoded',
  },
  completion: {
    heading: 'ALL FILES DECRYPTED',
    itemText: 'Lab Keycard acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `Our intel got scrambled during transmission. I need you to decode these words for me, ${CONFIG.recipientName}. Each one is a piece of the puzzle. Click the letters in the right order to spell each word.`,
    complete: 'Excellent work, Agent. The last word... was your name all along. This mission was always about you. Lab Keycard acquired. Let\'s keep moving.',
  },
};

// ─── Stage 6: Safe Cracker ───
export const PUZZLE_2_SAFE_CRACKER = {
  checkpoint: 'Checkpoint 03',
  title: 'Vault Access',
  subtitle: 'Combination Lock // 8-Digit Code Required',
  hud: {
    securityLevel: 'Security Level: Maximum',
    vaultSystem: 'Vault System',
    vaultAccess: 'Vault Access',
    terminalHeader: 'Umbrella Corp Vault System v4.2',
    vaultLockSystem: 'Vault Lock System',
    attLabel: 'ATT:',
    codeLabel: 'CODE:',
  },
  tutorial: {
    heading: 'Vault Briefing',
    subheading: 'Review the vault system before attempting entry',
    howItWorks: 'How It Works',
    steps: [
      'Use arrows to adjust each digit (0-9)',
      'Enter the correct 8-digit combination',
      'Submit to check — you get feedback on each digit',
    ],
    feedbackGuide: 'Feedback Guide',
    feedbackItems: [
      { label: 'Go Higher', desc: 'Number too low' },
      { label: 'Go Lower', desc: 'Number too high' },
      { label: 'Correct', desc: 'Digit locked in' },
    ],
    hint: 'Hint Removed for difficulty...  ',
    startButton: 'Start Cracking',
  },
  feedback: {
    goHigher: 'Go Higher',
    goLower: 'Go Lower',
    correct: 'Correct',
  },
  hints: {
    initial: 'Removed for difficulty...  ',
    afterThree: 'Removed for difficulty...  ',
    almostThere: 'Removed for difficulty...  ',
  },
  submitButton: 'Submit Code',
  submitting: 'Verifying...',
  completion: {
    heading: 'VAULT UNLOCKED',
    itemText: 'Vault Key acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `There's a vault up ahead with an 8-digit combination lock. I'll give you hints after each attempt -- I'll tell you if each number needs to go higher or lower. Focus ${CONFIG.recipientName}...`,
    complete: `The combination was 22-03-2006. A date that changed everything... the day you came into this world. Vault Key acquired, Agent. You're doing amazing.`,
  },
};

// ─── Stage 7: Memory Match ───
export const PUZZLE_3_MEMORY_MATCH = {
  checkpoint: 'Checkpoint 05',
  title: 'Security Clearance',
  subtitle: 'Pattern Recognition // Match the classified symbols',
  hud: {
    patternScanActive: 'PATTERN SCAN ACTIVE',
    moves: 'MOVES:',
    pairs: 'PAIRS:',
  },
  tutorial: {
    heading: 'Security Protocol',
    subheading: 'Review clearance test procedures',
    howItWorks: 'How It Works',
    steps: [
      'Click any card to reveal the hidden symbol',
      'Flip two cards per turn — find matching pairs',
      'Matched pairs stay revealed — clear all 8 pairs',
    ],
    cardStates: 'Card States',
    cardStateItems: [
      { label: 'Hidden', desc: 'Classified data' },
      { label: 'Revealed', desc: 'Temporarily shown' },
      { label: 'Matched', desc: 'Pair confirmed' },
    ],
    ratingNote: 'Performance rated by move count — aim for S rank!',
    startButton: 'Begin Scan',
  },
  cardFront: 'CLASSIFIED',
  legend: {
    hidden: 'Hidden',
    revealed: 'Revealed',
    matched: 'Matched',
  },
  ratings: {
    s: { letter: 'S', label: 'PERFECT' },
    a: { letter: 'A', label: 'EXCELLENT' },
    b: { letter: 'B', label: 'GOOD' },
    c: { letter: 'C', label: 'CLEARED' },
  },
  completion: {
    heading: 'CLEARANCE GRANTED',
    stats: { moves: 'MOVES', time: 'TIME', rating: 'RATING' },
    itemText: 'Security Badge acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `Security checkpoint ahead, ${CONFIG.recipientName}. They've set up a pattern recognition test. Match the symbols in pairs to prove your clearance level. Take your time .. I know you've got a good memory.`,
    complete: (moves) => `Clearance granted in just ${moves} moves. Impressive. Security Badge Level 3 acquired. You're getting closer to the vault, Agent.`,
  },
};

// ─── Stage 8: Laser Grid ───
export const LASER_GRID = {
  checkpoint: 'Checkpoint 06',
  title: 'Laser Corridor',
  subtitle: 'Security System // Navigate to the exit',
  hud: {
    hudLabel: 'CHECKPOINT 06 // LASER CORRIDOR',
    threatLevels: { nominal: 'NOMINAL', elevated: 'ELEVATED', critical: 'CRITICAL' },
    steps: 'STEPS:',
    resets: 'RESETS:',
    danger: 'DANGER',
  },
  tutorial: {
    heading: 'Corridor Protocol',
    howItWorks: 'How It Works',
    steps: [
      'Click adjacent cells to move through the grid',
      'Reach the EXIT at the bottom-right corner',
      'Hidden lasers are scattered throughout the corridor',
    ],
    symbolsGuide: 'Symbols Guide',
    symbols: [
      { label: 'PLAYER', desc: 'Your position' },
      { label: 'EXIT', desc: 'Destination' },
      { label: 'SAFE', desc: 'Visited path' },
      { label: 'LASER', desc: 'Hidden trap' },
    ],
    warning: 'HITTING A LASER RESETS YOU TO START',
    startButton: 'Enter Corridor',
  },
  grid: { start: 'START', exit: 'EXIT' },
  legend: [
    { label: 'You' },
    { label: 'Exit' },
    { label: 'Clickable' },
    { label: 'Laser (Hidden)' },
  ],
  messages: {
    laserDetected: 'LASER DETECTED -- RESETTING POSITION',
  },
  completion: {
    heading: 'CORRIDOR CLEARED',
    stats: { resets: 'RESETS', steps: 'STEPS' },
    itemText: 'Access Chip acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `Careful, ${CONFIG.recipientName}. This corridor is rigged with laser tripwires. Navigate from the top-left to the EXIT at the bottom-right. Click adjacent tiles to move. Hit a laser and you'll be sent back to the start. The lasers are hidden, so step carefully...`,
    complete: (resets) => `Through the laser grid${resets > 0 ? ` with only ${resets} reset${resets > 1 ? 's' : ''}` : ' without a single reset'}. Impressive footwork, ${CONFIG.recipientName}. Access Chip acquired. Deeper into the facility we go.`,
  },
};

export const HERB_MIXING = {
  leon: {
    intro: `We've found the Umbrella chemical lab. I need you to mix some compounds, ${CONFIG.recipientName}. Follow the hints to add herbs in the correct order. Green heals, Red powers up, Blue cures. Get all three formulas right.`,
    complete: `Perfect synthesis. You'd make a great field nurse, ${CONFIG.recipientName}. First Aid Kit secured. We'll need this for what's ahead.`
  }
};

// ─── Stage 9: Cipher Decode ───
export const PUZZLE_4_CIPHER_DECODE = {
  checkpoint: 'Checkpoint 07',
  title: 'Intel Decryption',
  subtitle: 'Caesar Cipher // Shift the alphabet to decode',
  targetText: `I WOV HOW SMART MY BABY ARE :3`,
  shift: 20,
  hud: {
    hudLabel: 'CHECKPOINT 07 // INTEL DECRYPTION',
    shiftLabel: 'SHIFT:',
    adjLabel: 'ADJ:',
  },
  tutorial: {
    heading: 'Decryption Protocol',
    howItWorks: 'How It Works',
    steps: [
      'The message is encrypted with a Caesar cipher — each letter shifted by a fixed amount',
      'Use the wheel, buttons, or slider to adjust the shift value (0-25)',
      'When the decoded text makes sense, confirm to complete decryption',
    ],
    example: 'Example',
    exampleOriginal: 'ORIGINAL',
    exampleShift: 'SHIFT +3',
    exampleResult: 'RESULT',
    warning: '26 POSSIBLE SHIFT VALUES -- ONLY ONE REVEALS THE MESSAGE',
    startButton: 'Begin Decryption',
  },
  puzzle: {
    encryptedLabel: 'Encrypted Transmission',
    shiftValueLabel: 'SHIFT VALUE',
    decodedLabel: 'Decoded Output',
    decryptionComplete: 'Decryption Complete',
    confirmButton: 'Confirm Decode',
    hint: 'Adjust the shift until the message becomes clear...',
  },
  completion: {
    heading: 'MESSAGE DECODED',
    stats: { shiftFound: 'SHIFT FOUND', adjustments: 'ADJUSTMENTS' },
    itemText: 'Encrypted Map acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `We intercepted an encrypted message, ${CONFIG.recipientName}. It's a Caesar cipher -- each letter has been shifted. Use the wheel or slider to find the right shift value and reveal the hidden message. I have a feeling it's something personal...`,
    complete: `The encrypted intel was a number wish all along, i wonder what it means. Encrypted Map acquired. We're getting closer to the vault.`,
  },
};

// ─── Stage 10: Lockpick ───
export const LOCKPICK = {
  checkpoint: 'Checkpoint 08',
  title: 'Lockpick',
  subtitle: 'Hold & release in the sweet spot',
  hud: {
    hudLabel: 'CHECKPOINT 08 // LOCKPICK',
    pinLabel: 'PIN:',
    difficultyLevels: ['BASIC', 'MODERATE', 'ADVANCED', 'EXPERT', 'MASTER'],
  },
  tutorial: {
    heading: 'How It Works',
    steps: [
      'Hold to apply tension — the pick moves across the bar',
      'Release when the pick is inside the green sweet spot zone',
      'Each pin gets faster with a narrower sweet spot',
    ],
    pickBar: 'Pick Bar',
    sweetSpot: 'SWEET SPOT',
    warning: 'MISS TOO MANY TIMES AND YOUR PICK BREAKS',
    startButton: 'Begin Picking',
  },
  puzzle: {
    picksLabel: 'PICKS',
    holdToTension: 'HOLD TO TENSION',
    holding: 'HOLDING...',
    releaseNow: 'RELEASE NOW',
    lockJammed: 'LOCK JAMMED — RESTARTING FROM PIN 1',
  },
  completion: {
    heading: 'LOCK PICKED',
    stats: { pinsPicked: 'PINS PICKED', picksBroken: 'PICKS BROKEN' },
    itemText: 'Master Key acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `This door has a five-pin tumbler, ${CONFIG.recipientName}. Hold to apply tension, then release when the pick is in the sweet spot. Miss too many times and the pick breaks. Each pin gets tighter, so stay sharp.`,
    complete: `Five pins, all picked clean. Where did you learn that? Master Key acquired, ${CONFIG.recipientName}. Almost there now.`,
  },
};

// ─── Stage 11: Wire Puzzle ───
export const PUZZLE_5_WIRE_PUZZLE = {
  checkpoint: 'Checkpoint 09',
  title: 'Power Grid',
  subtitle: 'Emergency Restoration // Connect matching wires',
  hud: {
    hudLabel: 'CHECKPOINT 09 // POWER GRID',
    connections: 'CONNECTIONS',
    faults: 'FAULTS',
  },
  tutorial: {
    heading: 'POWER GRID PROTOCOL',
    subheading: 'EMERGENCY WIRE RESTORATION',
    procedure: 'PROCEDURE',
    steps: [
      { n: '01', text: 'Memorize wire positions during the 3-SECOND PREVIEW' },
      { n: '02', text: 'Right-side colors will HIDE — match from memory' },
      { n: '03', text: 'Connect all 5 wires to restore power' },
    ],
    wireIdentification: 'WIRE IDENTIFICATION',
    warnings: [
      'WRONG MATCH SCRAMBLES PORT POSITIONS',
      '3 CONSECUTIVE MISSES = FULL RESET',
    ],
    startButton: 'RESTORE POWER',
  },
  puzzle: {
    junctionBox: 'JUNCTION BOX',
    selectMatchingPort: 'SELECT MATCHING PORT',
    memorize: 'MEMORIZE — ',
    colorMismatch: 'COLOR MISMATCH — PORTS SCRAMBLING...',
    colorMismatchReset: 'COLOR MISMATCH — SYSTEM RESETTING...',
    systemReset: 'SYSTEM RESET — ALL WIRES DISCONNECTED',
    instructionsBefore: 'Click a wire on the left, then click its matching color on the right',
    instructionsAfter: 'Right-side colors are hidden — match from memory',
  },
  completion: {
    heading: 'POWER RESTORED',
    stats: { connections: 'CONNECTIONS', faults: 'FAULTS' },
    itemText: 'Power Cell acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `The power grid is offline, ${CONFIG.recipientName}. We need to reconnect the emergency wires to proceed. But the system's damaged — the port labels will only show briefly before going dark. You'll need to memorize the layout. And be careful... wrong connections will scramble the ports.`,
    complete: "Power's back online. You're a natural at this. Power Cell acquired. One more checkpoint to go... the main vault is right ahead.",
  },
};

// ─── Stage 12: Radio Tuning ───
export const RADIO_TUNING = {
  checkpoint: 'Checkpoint 10',
  title: 'Radio Contact',
  subtitle: 'Locate Leon\'s Transmission Signal',
  hud: {
    hudLabel: 'CHECKPOINT 10 // RADIO CONTACT',
    lockStatus: { locked: 'LOCKED', detected: 'DETECTED', noSignal: 'NO SIGNAL' },
  },
  tutorial: {
    heading: 'FREQUENCY PROTOCOL',
    subheading: 'SECURE CHANNEL ACQUISITION',
    procedure: 'PROCEDURE',
    steps: [
      { n: '01', text: 'Adjust the FREQUENCY DIAL to locate the signal' },
      { n: '02', text: 'Watch the WAVEFORM and SIGNAL METER for strength' },
      { n: '03', text: 'LOCK the signal when the waveform stabilizes' },
    ],
    intel: 'INTEL',
    targetFrequency: 'TARGET FREQUENCY',
    targetFrequencyValue: 'UNKNOWN',
    warning: 'SIGNAL NOISE INCREASES AT WRONG FREQUENCIES',
    startButton: 'BEGIN SCAN',
  },
  waveform: {
    label: 'WAVEFORM',
    locked: 'SIGNAL LOCKED',
    scanning: 'SCANNING...',
  },
  radioUnit: {
    company: 'UMBRELLA CORP',
    model: 'TRC-4500 TACTICAL RADIO',
  },
  frequencyDisplay: {
    mhz: 'MHz',
    locked: 'LOCKED',
    detected: 'SIGNAL DETECTED',
    noSignal: 'NO SIGNAL',
    optimalSignal: '✓ Signal optimal - Ready to lock!',
    gettingCloser: '◎ Getting closer...',
    tuneToFind: '○ Tune to find the signal',
  },
  controls: {
    frequencyControl: 'FREQUENCY CONTROL',
    signalStrength: 'SIGNAL STRENGTH',
    lockButton: 'Lock Signal',
  },
  transmission: {
    incoming: 'INCOMING TRANSMISSION',
    receiving: 'RECEIVING...',
    complete: 'TRANSMISSION COMPLETE',
    proceedButton: 'PROCEED',
  },
  messageLines: [
    `...${CONFIG.recipientName}... can you hear me?`,
    'This is Leon. If you\'re receiving this...',
    '...then you\'re closer than anyone has ever been.',
    'The vault is just ahead.',
    'Everything beyond this point... was made for you.',
    `${CONFIG.senderName} wanted you to know...`,
    '...you are the mission. You always were.',
    'Signal Beacon locked. Proceed to the final checkpoint.',
  ],
  completion: {
    heading: 'SIGNAL LOCKED',
    stats: { signal: 'SIGNAL', frequency: 'FREQUENCY' },
    signalValue: '100%',
    frequencyValue: '87.3 MHz',
    itemText: 'Signal Beacon acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `I'm going to try to reach you on a secure channel, ${CONFIG.recipientName}. Tune the radio dial until you find my signal. Watch the waveform and signal strength — you'll know when you're close. When everything aligns... I have something important to tell you.`,
    complete: `You found me, ${CONFIG.recipientName}. Signal Beacon acquired. One final door stands between you and the end of this mission. Everything that comes next... is your reward.`,
  },
};

// ─── Stage 13: Music Box Melody ───
export const MUSIC_BOX_MELODY = {
  checkpoint: 'Checkpoint 12',
  title: 'Music Box Melody',
  subtitle: 'Listen carefully, then play it back',
  hud: {
    hudLabel: 'CHECKPOINT 12 // MUSIC BOX',
  },
  tutorial: {
    heading: 'MUSIC BOX PROTOCOL',
    subheading: 'MELODY SEQUENCE CHALLENGE',
    procedure: 'PROCEDURE',
    steps: [
      { n: '01', text: 'Press PLAY to hear the melody sequence' },
      { n: '02', text: 'Play it back by clicking the colored KEYS' },
      { n: '03', text: 'Complete all 3 rounds to unlock the box' },
    ],
    noteIdentification: 'NOTE IDENTIFICATION',
    keyboard: 'KEYBOARD: PRESS C, D, E, F, G, A, B',
    startButton: 'OPEN MUSIC BOX',
  },
  gameplay: {
    listening: 'LISTENING...',
    playMelody: 'PLAY MELODY',
    correctFeedback: '♪ Melody matched beautifully!',
    wrongFeedback: '♪ Wrong note... listen again',
    keyboardHint: 'Press C, D, E, F, G, A, B keys or click',
  },
  completion: {
    heading: 'THE BOX OPENS',
    wrongNotes: 'WRONG NOTES',
    rounds: 'ROUNDS',
    flavor: 'Inside, a small note reads:',
    message: `"Every melody reminds me of you, ${CONFIG.recipientName}."`,
    itemText: 'Music Box Key acquired',
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `${CONFIG.recipientName}, there's an old music box here. It seems to be locked with a melody. Listen to the tune and play it back. If you get it right, it might just open for you.`,
  },
};

// ─── Stage 14: Final Lock ───
export const PUZZLE_6_FINAL_LOCK = {
  checkpoint: 'FINAL CHECKPOINT',
  title: 'MAIN VAULT',
  subtitle: 'Place all 11 items to unlock',
  hud: {
    hudLabel: 'FINAL CHECKPOINT // MAIN VAULT',
  },
  tutorial: {
    heading: 'VAULT AUTHORIZATION',
    subheading: 'FINAL SECURITY CLEARANCE',
    procedure: 'PROCEDURE',
    steps: [
      { n: '01', text: 'Select an item from your INVENTORY below' },
      { n: '02', text: 'Place it in the matching VAULT SLOT on the ring' },
      { n: '03', text: 'Fill all 11 slots to unseal the vault' },
    ],
    slotIdentification: 'SLOT IDENTIFICATION',
    warning: 'WRONG PLACEMENTS ARE TRACKED — PRECISION MATTERS',
    startButton: 'INITIATE SEQUENCE',
  },
  gameplay: {
    collectedItems: 'COLLECTED ITEMS',
    selected: 'Selected:',
    hint: 'Select an item above, then place it in the matching slot',
    incorrectSlot: 'INCORRECT SLOT',
    vaultAuthorization: 'VAULT AUTHORIZATION',
    terminal: {
      header: 'VAULT_SYSTEM v2.1 — AUTHORIZATION LOG',
      awaiting: '> Awaiting item placement...',
      verified: 'VERIFIED ✓',
      rejected: 'REJECTED ✗',
    },
  },
  completion: {
    heading: 'VAULT UNSEALED',
    wrongAttempts: 'WRONG ATTEMPTS',
    items: 'ITEMS',
    flavor: 'The vault door swings open, revealing:',
    message: `"Everything behind this door was always meant for you, ${CONFIG.recipientName}."`,
  },
  proceedButton: 'PROCEED',
  leon: {
    intro: `This is it, ${CONFIG.recipientName}. The main vault -- 11 locks, 11 items. You've collected everything across every checkpoint. Select each item from your inventory and place it in its matching slot. Once all eleven are in place... the vault opens forever.`,
    complete: `The vault is open. You did it, Agent ${CONFIG.recipientName}. Eleven checkpoints, eleven items — and not a single failure. Now... step inside and claim what's been waiting for you all along.`,
  },
};

// ─── Stage 15: Escape Sequence ───
export const ESCAPE_SEQUENCE = {
  breach: {
    heading: 'SYSTEM BREACH',
    status: 'UMBRELLA MAINFRAME COMPROMISED',
    message: 'Facility self-destruct initiated...',
    sequence: [
      { text: 'NO TURNING BACK', delay: 2.2 },
      { text: 'GET OUT NOW', delay: 3.1 },
      { text: 'Run,', delay: 4.0 },
      { text: 'RUN!', delay: 4.6 },
    ],
  },
  running: {
    selfDestructIn: 'SELF-DESTRUCT IN',
    spamSpace: '⎵ SPAM SPACE ⎵',
    instruction: 'OR TAP ANYWHERE TO RUN',
    corridor: { collapse: 'COLLAPSE', exit: 'EXIT' },
  },
  survived: {
    status: 'status: escaped',
    heading: 'YOU MADE IT',
    flavor1: 'The facility crumbles behind you.',
    flavor2: 'But something awaits you on the other side.',
    proceedButton: 'proceed',
  },
  dead: {
    heading: 'TOO SLOW',
    message: 'The facility collapsed on you... try again!',
    retryButton: 'TRY AGAIN',
  },
};

// ─── Stage 16: Grand Reveal ───
export const GRAND_REVEAL = {
  checkpoint: 'FINAL STAGE // GRAND REVEAL',
  status: 'PROCESSING',
  transition: {
    heading: 'MISSION COMPLETE',
    messages: [
      { text: '> All objectives secured', delay: 1.0 },
      { text: '> DECRYPTING FINAL PACKAGE...', delay: 1.8 },
      { text: '> Encryption key verified', delay: 2.5 },
      { text: '> Initiating celebration protocol...', delay: 3.2 },
    ],
  },
  consoleLines: [
    { t: 0.0, icon: '♡', text: 'Initializing celebration protocol...', color: '#f9a8d4' },
    { t: 0.8, icon: '◈', text: 'Loading confetti modules ██████████ OK', color: '#c4b5fd' },
    { t: 1.6, icon: '✦', text: 'Decrypting your surprise package...', color: '#fbbf24' },
    { t: 2.4, icon: '♡', text: 'Hearts: 999+ (overflow detected, that\'s a lot of love)', color: '#f472b6' },
    { t: 3.2, icon: '◌', text: 'Scanning memory banks for happy moments... found 1,000,000+', color: '#a78bfa' },
    { t: 4.0, icon: '⟡', text: 'Compiling compliments ━━━━━━━━━━━━━━ 100%', color: '#fde68a' },
    { t: 4.8, icon: '♡', text: 'Installing smile.exe... done ✓', color: '#f9a8d4' },
    { t: 5.5, icon: '◈', text: 'Warming up virtual hugs generator...', color: '#c4b5fd' },
    { t: 6.2, icon: '✦', text: 'Butterflies in stomach: ACTIVATED', color: '#fbbf24' },
    { t: 7.0, icon: '♡', text: 'Loading the sweetest prize ever curated...', color: '#f472b6' },
    { t: 7.8, icon: '◌', text: 'Running adorableness check... [PASSED] you\'re cute', color: '#a78bfa' },
    { t: 8.5, icon: '⟡', text: 'Deploying rose petals ━━━━━━━━━━ deployed!', color: '#fca5a5' },
    { t: 9.2, icon: '♡', text: 'Encrypting love letter with AES-256-HEART...', color: '#f9a8d4' },
    { t: 10.0, icon: '◈', text: 'Fetching reasons why you\'re amazing... (list too long)', color: '#c4b5fd' },
    { t: 10.8, icon: '✦', text: 'Building your special chapter... almost ready', color: '#fbbf24' },
    { t: 11.5, icon: '♡', text: 'Final checks ██████████████████ ALL CLEAR', color: '#f472b6' },
    { t: 12.2, icon: '⟡', text: 'Package verified. Zero bugs, 100% love.', color: '#fde68a' },
    { t: 13.0, icon: '♡', text: '', color: '#f9a8d4', special: 'ready' },
  ],
  console: {
    ready: '✓ All systems ready. Your surprise is waiting!',
    progress: 'PROGRESS',
    running: 'RUNNING',
    complete: 'COMPLETE',
    programName: 'celebration.exe',
  },
  continueButton: 'Continue to Your Surprise',
};

// ─── Identity Quiz ───
// types: multiple_choice, text_input, runaway, morph, only_correct
export const IDENTITY_QUIZ = {
  title: 'IDENTITY VERIFICATION',
  subtitle: `Confirm you are the real ${CONFIG.recipientCodename}`,
  maxAttempts: 3,
  questions: [
    {
      id: 'q1', type: 'multiple_choice',
      question: "How old are you now?",
      options: ['19', '20', '21', '22'],
      answer: '20',
      hint: 'REVEAL YOUR EST',
    },
    {
      id: 'q2', type: 'text_input',
      question: "What's the nickname I always call you?",
      answer: 'Pok',
      acceptAlso: ['babe', 'bby', 'pookie', 'bae', 'baby', 'babes', 'bby girl', 'baby girl', 'bb'],
      placeholder: 'Type your nickname...',
    },
    {
      id: 'q3', type: 'multiple_choice',
      question: 'What do you always call me?',
      options: ['Daddy', 'Royal', 'Nasser', 'Rolah'],
      answer: 'Daddy',
      hint: 'Hi babygirl...',
    },
    {
      id: 'q4', type: 'multiple_choice',
      question: "What's your favorite color?",
      options: ['Blue', 'Black', 'Red', 'Pink'],
      answer: 'Pink',
      hint: 'Think light and cute...',
    },
    {
      id: 'q5', type: 'text_input',
      question: "What's your favourite kind of flowers?",
      answer: 'Lilies',
      acceptAlso: ['lily'],
      placeholder: 'Mhmm...?',
    },
    {
      id: 'q6', type: 'multiple_choice',
      question: "Where's our dream trip destination?",
      options: ['Paris', 'New York', 'Tokyo', 'Moscow'],
      answer: 'Tokyo',
      hint: 'Think night light...',
    },
    {
      id: 'q7', type: 'runaway',
      question: 'What\'s the biggest thing you\'ve ever seen and likey?',
      options: ['Your Numnom', 'You heart'],
      answer: 'Your Numnom',
    },
    {
      id: 'q8', type: 'multiple_choice',
      question: "What's my favorite thing about you?",
      options: ['Face', 'Personality', 'Body', 'All Options'],
      answer: 'All Options',
      hint: 'Think hard...',
    },
    {
      id: 'q9', type: 'runaway',
      question: 'Who is you daddy?',
      options: ['Daddy Royal', 'My biological father'],
      answer: 'Daddy Royal',
    },
    {
      id: 'q10', type: 'morph',
      question: 'Am I the best boyfriend ever?',
      options: ['Yes', 'No'],
      answer: 'Yes',
    },
    {
      id: 'q11', type: 'only_correct',
      question: 'Who is the most amazing, handsome, daddiest, strongest, most muscular and  best daddy of all time?',
      options: ['Dada Royal', 'Leon S. Kennedy', 'Kisuke Urahara', 'Arthur Morgan'],
      answer: 'Dada Royal',
    },
  ],
  scanning: {
    phase1: 'SCANNING BIOMETRICS...',
    phase2: 'ANALYZING IDENTITY...',
    phase3: 'VERIFICATION REQUIRED',
  },
  quiz: {
    attemptsLeft: 'LEFT',
    hintLabel: 'HINT:',
    hintRunaway: 'The right answer won\'t run away...',
  },
  lockout: {
    heading: 'ACCESS DENIED',
    status: 'IDENTITY VERIFICATION FAILED',
    message: 'Too many incorrect answers. System locked.',
    retryButton: 'RETRY VERIFICATION',
  },
  success: {
    heading: 'IDENTITY CONFIRMED',
    welcome: `Welcome back, ${CONFIG.recipientCodename}`,
    stats: [
      'Biometric match: 100%',
      'Clearance level: TOP SECRET',
      'Access granted to the birthday surprise',
    ],
    continueButton: 'Continue to Your Surprise',
  },
  hudStatus: {
    scanning: 'SCANNING',
    verifying: 'VERIFYING',
    confirmed: 'CONFIRMED',
    locked: 'LOCKED',
  },
};

export const GLOBAL_UI = {
  errorBoundary: {
    heading: 'SYSTEM MALFUNCTION',
    message: 'Something went wrong in this stage.',
    unknownError: 'Unknown error',
    retryButton: 'RETRY',
    skipButton: 'SKIP STAGE',
  },
  inventory: {
    title: 'Inventory',
    slotCount: (count, total) => `[${count}/${total}]`,
  },
  leon: {
    name: 'Leon S. Kennedy',
    shortName: 'Leon',
    clickToContinue: '[ Click to continue ]',
  },
  audio: {
    enableLabel: 'Enable Sound',
    muteLabel: 'Mute',
  },
};

// ─── Inventory UI ───
export const INVENTORY_UI = {
  header: 'Inventory',
  slots: [
    { id: 'lab_keycard', label: 'Keycard' },
    { id: 'tactical_scope', label: 'Scope' },
    { id: 'vault_key', label: 'Vault Key' },
    { id: 'first_aid_kit', label: 'Med Kit' },
    { id: 'security_badge', label: 'Badge' },
    { id: 'access_chip', label: 'Chip' },
    { id: 'encrypted_map', label: 'Map' },
    { id: 'master_key', label: 'Master' },
    { id: 'power_cell', label: 'Power' },
    { id: 'signal_beacon', label: 'Beacon' },
  ],
};
